import { pageTitle } from '@palaccount/config';
import type { EChartsOption } from 'echarts';
import { Info } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TableCell } from '@/components/ui/table';
import { formatMoney } from '@/lib/format';

import { Amount, Days, Ltr, Pct, Qty } from '@/components/amount';
import { Chart } from '@/components/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBaseCurrency, useCompanyToday } from '@/lib/session';
import { cn } from '@/lib/utils';

import type { ReportConfigBody } from '@palaccount/contracts';

import { useWarehouses } from '@/features/inventory/stock-api';

import { useInventoryReport } from './api';
import { configInForce } from './column-picker';
import { DetailTable, type DetailColumn } from './detail-table';
import { buildReportGrid, downloadXlsx } from './export';
import { DetailFold, MoreFilters, ReportToolbar, SummaryCards } from './report-shell';
import { ReportsTabs } from './reports-tabs';
import { WarehouseFilter } from './warehouse-filter';

/**
 * DSH-07 — Inventory Reports. A POINT-IN-TIME screen: unlike DSH-05/06 it
 * describes what is on the shelf right now, so the cards carry NO change pill
 * (BR-DSH-07-03) — stock is a state, not a flow.
 *
 * IT HAS NO PERIOD AT ALL, which is a ruling and not an omission. §5a used to
 * give it four presets rather than the shared six, and it drew the date pair
 * beside them; neither ever reached the query, because the figures do not vary
 * by window. Delegated ruling A14 (25 Aug 2026) settled that half-held state
 * by declaring the screen as-of-now and removing the boxes — see the long note
 * on BR-DSH-10-02 below, which is the rule this screen was the exception to.
 * The query sends the warehouse/category filters and the threshold, and those
 * are the whole of what narrows it.
 *
 * BR-DSH-07-14: a reader without `inventory.see_item_cost` gets this screen
 * REDUCED, never refused — the cost, value, turnover and ABC content is
 * absent from the payload (`costVisible: false`), and what is drawn here is
 * only the question of what to do with a hole. Nothing is hidden client-side,
 * because a figure that reached the browser is already out.
 */

/** BR-DSH-07-10 — exactly four buckets, colour rising with age. */
const AGING_COLORS: Readonly<Record<string, string>> = {
  '0-30': 'bg-emerald-500',
  '31-60': 'bg-blue-500',
  '61-90': 'bg-amber-500',
  '90+': 'bg-red-500',
};

/** §5h — green A, blue B, grey C. */
const CLASS_CHIPS: Readonly<Record<string, string>> = {
  A: 'bg-emerald-100 text-emerald-700',
  B: 'bg-blue-100 text-blue-700',
  C: 'bg-muted text-muted-foreground',
};

type DetailRow = {
  itemId: string;
  code: string;
  name: string;
  category: string;
  onHand: string;
  /**
   * `N-4` — the item's stock unit. Carried on the row rather than looked up
   * because the detail grid is assembled from three different blocks of the
   * response and only the valuation block knows it.
   */
  unitSymbol: string;
  unitPrecision: number;
  /** Absent, with turnover and class, on the reduced screen (BR-DSH-07-14). */
  totalValue: string | null;
  /** That item's category turnover — the contract carries turnover per category. */
  turnover: string | null;
  daysIdle: number | null;
  class: 'A' | 'B' | 'C' | null;
};

const DEFAULT_COLUMNS = ['code', 'name', 'category', 'onHand', 'totalValue', 'turnover', 'daysIdle', 'class'];
/** BR-DSH-07-14's reduced set: what is left when the money is taken out. */
const COLUMNS_WITHOUT_COST = ['code', 'name', 'category', 'onHand', 'daysIdle'];

export function InventoryReportPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  /**
   * The company's today — this screen's ONLY date, and it is an as-at stamp
   * rather than a window. N-16/EC-19-65: one today across the whole system,
   * read from the company's time zone and never the reader's machine.
   */
  const today = useCompanyToday();
  /** §5g — the typed threshold. Kept as TEXT so a word can fall back to 60. */
  const [thresholdText, setThresholdText] = useState('60');
  const [deadSort, setDeadSort] = useState<'days' | 'value'>('days');
  const [columns, setColumns] = useState<string[]>(DEFAULT_COLUMNS);

  /**
   * ERR-DSH-07-07 / ERR-DSH-07-02 / §5g: "Typed a word into the threshold box
   * — the system FALLS BACK TO 60 days." Anything that is not a whole number
   * silently becomes 60, and silently is the spec's own choice: the box is a
   * dial rather than a form field, and refusing a half-typed value would empty
   * the table while somebody was still typing. The fallback is safe precisely
   * because 60 is the delivered default, so the worst outcome is the report
   * the reader started from.
   *
   * The text is kept as TEXT and only READ as a number, which is what makes
   * the fallback possible at all — a numeric input would have coerced the word
   * to NaN and drawn a table of nothing.
   */
  const threshold = /^\d+$/.test(thresholdText.trim()) ? Number(thresholdText.trim()) : 60;
  /** BR-DSH-10-17's filter. `null` is "All warehouses", the delivered default. */
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  /** BR-DSH-10-16 / DSH-10 Band 6 — the applied configuration (see DSH-05). */
  const [appliedConfig, setAppliedConfig] = useState<{ name: string; body: ReportConfigBody } | null>(null);
  /*
   * BR-DSH-10-07 — both narrowings ride in the same query key, so adding the
   * warehouse cannot displace the threshold the reader already typed.
   *
   * ── BR-DSH-10-02, AND WHY THIS SCREEN IS THE EXCEPTION THAT PROVES IT ────
   *
   * *"Every report must accept a from date and a to date, and every figure on
   * the page must cover exactly that window."* DSH-05, DSH-06 and DSH-08 do,
   * and this screen used to be the reason the rule was only HALF-HELD: it drew
   * two date boxes and a preset row while `reportsContract.inventory` has no
   * date parameters and `ReportsService.inventory()` takes none. The window
   * was accepted and no figure covered it.
   *
   * THE READER-FACING HALF WAS THE SHARPER ONE: two date boxes and a lit
   * preset button are a promise. A reader who narrowed this screen to one
   * month saw every figure stay exactly where it was, with no way to tell
   * "nothing changed in that month" from "the dates do nothing" — the same
   * shape as the inert filters ERR-DSH-05-06 named on the sales screen.
   *
   * It was not a plain bug, which is why a previous wave reported it instead
   * of fixing it: DSH-10 Band 1 gives Inventory four presets rather than six
   * and explains why — *"most of its content is a picture of the stock you are
   * holding right now, and 'today' or 'this week' would not change it"* —
   * while turnover reads year-to-date movement and the ageing and dead-stock
   * bands are measured against the company's today. Rule and band note pulled
   * in different directions, and choosing between them would have silently
   * redefined the screen's headline numbers.
   *
   * RULED, delegated ruling A14 (25 Aug 2026), option (a): *declare DSH-07
   * as-of-now and exempt it like the cash-flow report — the smallest honest
   * fix*, with the date boxes removed. So the boxes are gone (`window={null}`
   * on the toolbar below), the grid sends no window, and the printed heading
   * block states the run time instead — which is the whole of the window a
   * point-in-time report has.
   *
   * The rule is therefore satisfied product-wide rather than nearly: every
   * report that HAS a window covers exactly it, and the one report that does
   * not have one no longer claims to. Removing the promise is what made the
   * rule true; leaving the boxes and honouring them was option (c), a real
   * as-at valuation feature, and A14 priced it separately.
   */
  const { data, isPending, isError } = useInventoryReport({ threshold, ...(warehouseId ? { warehouseId } : {}) });

  // BR-DSH-07-14: the picker itself loses the money columns, so nobody is
  // offered a column that would only ever draw an em dash.
  useEffect(() => {
    if (data && !data.costVisible) setColumns(COLUMNS_WITHOUT_COST);
  }, [data]);

  useEffect(() => {
    document.title = pageTitle(t('reports.tabInventory'));
  }, [t]);

  const donutOption = useMemo<EChartsOption>(() => {
    const cats = data?.valueByCategory ?? [];
    return {
      tooltip: { trigger: 'item' },
      series: [{ type: 'pie', radius: ['45%', '75%'], data: cats.map((c) => ({ name: c.name, value: Number(c.value) })) }],
    };
  }, [data?.valueByCategory]);

  /** §5g — two sorts: longest idle first, or most money frozen first. */
  const deadStock = useMemo(() => {
    const rows = [...(data?.deadStock ?? [])];
    return rows.sort((a, b) =>
      deadSort === 'days'
        ? b.daysIdle - a.daysIdle
        : Number(b.tiedUpValue ?? 0) - Number(a.tiedUpValue ?? 0),
    );
  }, [data?.deadStock, deadSort]);

  /** §5h — the count in each class, which must add up to SKUs tracked. */
  const classCounts = useMemo(() => {
    const counts = { A: 0, B: 0, C: 0 };
    for (const row of data?.abc ?? []) counts[row.class] += 1;
    return counts;
  }, [data?.abc]);

  /** §5i joins valuation to the ABC class, the idle days and the category turnover. */
  const detailRows = useMemo<DetailRow[]>(() => {
    const classBy = new Map((data?.abc ?? []).map((r) => [r.itemId, r.class]));
    const idleBy = new Map((data?.deadStock ?? []).map((r) => [r.itemId, r.daysIdle]));
    const turnoverBy = new Map((data?.turnoverByCategory ?? []).map((r) => [r.name, r.turnover]));
    return (data?.valuation ?? []).map((row) => ({
      itemId: row.itemId,
      code: row.code,
      name: row.name,
      category: row.category,
      onHand: row.onHand,
      unitSymbol: row.unitSymbol,
      unitPrecision: row.unitPrecision,
      totalValue: row.totalValue ?? null,
      turnover: turnoverBy.get(row.category) ?? null,
      daysIdle: idleBy.get(row.itemId) ?? null,
      class: classBy.get(row.itemId) ?? null,
    }));
  }, [data]);

  const detailColumns: DetailColumn<DetailRow>[] = [
    { id: 'code', label: t('reports.colCode'), render: (r) => <Ltr value={r.code} className="font-mono text-xs" /> },
    { id: 'name', label: t('reports.colItem'), render: (r) => r.name },
    { id: 'category', label: t('reports.colCategory'), render: (r) => r.category },
    {
      id: 'onHand',
      label: t('reports.colOnHand'),
      numeric: true,
      // `N-4` on the CELL. The XLSX path below reads `row.onHand` raw, on
      // purpose: this column is exported as a NUMBER, and a spreadsheet cell
      // reading "200.000 kg" is text that no formula can add up. The rule is
      // about what a screen shows.
      render: (r) => <Qty value={r.onHand} precision={r.unitPrecision} unit={r.unitSymbol} />,
    },
    {
      id: 'totalValue',
      label: t('reports.colTotalValue'),
      numeric: true,
      render: (r) => (r.totalValue === null ? '—' : <Amount value={r.totalValue} currency={baseCurrency} />),
    },
    {
      id: 'turnover',
      label: t('reports.colTurnover'),
      numeric: true,
      render: (r) => (r.turnover === null ? '—' : <Ltr value={`${r.turnover}×`} className="font-mono" />),
    },
    {
      id: 'daysIdle',
      label: t('reports.colDaysIdle'),
      numeric: true,
      render: (r) => (r.daysIdle === null ? '—' : <Days value={r.daysIdle} className="font-mono" />),
    },
    {
      id: 'class',
      label: t('reports.colClass'),
      render: (r) =>
        r.class === null ? '—' : <span className={cn('rounded-full px-2 py-0.5 text-xs', CLASS_CHIPS[r.class])}>{r.class}</span>,
    },
  ];

  /**
   * What is on screen, as one grid. DSH-07's cards carry NO change pill
   * (BR-DSH-07-03) — stock is a state, not a flow — so `changePct` is absent
   * here rather than zero.
   */
  const rtl = i18n.language === 'ar';
  /**
   * BR-DSH-10-16 — DSH-07's "filters applied" are the warehouse AND the
   * dead-stock threshold: §5g's typed box narrows the dead-stock table and
   * the summary card, so a printed sheet that did not state it would show a
   * "Dead / slow items" count nobody could reproduce. Stated as the screen
   * states it: "No movement in — 60 days".
   */
  const { data: warehouses } = useWarehouses({ status: 'active' });
  const warehouseInForce =
    (warehouseId && warehouses?.find((w) => w.id === warehouseId)?.name) || t('reports.allWarehouses');
  const currentBody: ReportConfigBody = {
    columns,
    filters: { threshold: String(threshold) },
    // A14: no window to save. `asOfNow` is the preset a saved configuration
    // records for this screen — the field is required by the contract, and a
    // stored `thisYear` would be a period this report never had.
    period: { preset: 'asOfNow' },
  };
  const configName =
    appliedConfig !== null && configInForce(appliedConfig.body, currentBody, { hasWindow: false })
      ? appliedConfig.name
      : null;
  const { grid, types } = buildReportGrid<DetailRow>({
    title: t('reports.tabInventory'),
    // A14 — no `from`/`to`: this sheet is as at its run time, and the heading
    // block prints that. `ReportGridSchema` refuses a half-window, so omitting
    // one without the other is not expressible.
    filters: [
      { label: t('reports.filterWarehouse'), value: warehouseInForce },
      { label: t('reports.noMovementIn'), value: `${threshold} ${t('reports.days')}` },
    ],
    configName,
    cards: data
      ? [
          ...(data.summary.totalValue === undefined
            ? []
            : [{ label: t('reports.cardStockValue'), value: formatMoney(data.summary.totalValue, 'USD') }]),
          { label: t('reports.cardSkus'), value: String(data.summary.skusTracked) },
        ]
      : [],
    columnDefs: detailColumns,
    selected: columns,
    defaults: DEFAULT_COLUMNS,
    rows: detailRows,
    value: (row, id) => String(row[id as keyof DetailRow] ?? ''),
  });

  return (
    <div className="flex flex-col gap-4">
      <ReportsTabs />
      <h1 className="text-xl font-semibold">{t('reports.tabInventory')}</h1>

      <ReportToolbar
        /*
         * THE EXEMPTION, AND IT IS A RULING RATHER THAN AN OMISSION.
         *
         * `window={null}` draws no presets and no date pair, because DSH-07 is
         * as-of-now. See the long note above for the ruling (A14) and for what
         * the two boxes were doing before it.
         */
        window={null}
        grid={data ? grid : null}
        onExcel={() =>
          // BR-DSH-10-24: the same grid Print and PDF use. The filename
          // carries the company's today, which is what this sheet is AS AT —
          // it used to carry the `to` box, a date the figures never honoured.
          downloadXlsx(
            `inventory-report-${today}.xlsx`,
            t('reports.tabInventory'),
            grid.columns,
            grid.rows,
            types,
            rtl,
          )
        }
      />

      {/*
        BR-DSH-07-15: "All three More filters dropdowns must limit every figure
        on the page", and ERR-DSH-07-04 / ERR-DSH-07-06 are the prototype
        defect it corrects — "the Category and Brand filters RENDER BUT DO NOT
        FILTER", §14's advice being "use the warehouse filter".

        The sweep that wrote this comment reported the gap rather than
        claiming either half: the SERVER takes `warehouseId` and `categoryId`
        and applies both to every figure (see `ReportsService.inventory`), so
        the half that was broken in the prototype was already sound, but this
        screen rendered no dropdown at all — only the hint below — so there
        was nothing to filter WITH and no inert control to mislead anyone
        either. A feature gap, not the defect, and worth having kept apart: a
        screen with no filter is honest while a screen with three that do
        nothing is not.

        THE WAREHOUSE THIRD OF IT IS NOW BUILT (BR-DSH-10-17), so the rule's
        "must limit every figure on the page" is true of that dropdown, end to
        end. Category is still not offered here, and Brand is not a dimension
        the product has — so this comment stays, one third shorter, rather
        than being deleted as if the whole row were done.
      */}
      <MoreFilters>
        <WarehouseFilter value={warehouseId} onChange={setWarehouseId} />
        <p className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-2">{t('reports.filtersHint')}</p>
      </MoreFilters>

      {isPending ? (
        <div className="h-60 animate-pulse rounded-md bg-muted" aria-busy />
      ) : isError || !data ? (
        <p role="alert" className="py-8 text-center text-sm text-destructive">{t('common.loadFailed')}</p>
      ) : (
        <>
          {/*
            ERR-DSH-07-03: "Ran the screen without cost permission — the screen
            runs with cost, value, turnover and ABC removed, AND A NOTE SAYING
            SO." BR-DSH-07-14 gives the first half and this line is the second,
            which was missing: the four blocks simply vanished, so a reader
            without the grant saw a shorter report with no reason for it and no
            way to tell an absent figure from a company that holds no stock.

            An absence that explains itself is the whole difference. The note
            names the remedy too (ask an administrator), because §14's own
            advice to this reader is that the permission exists and they do not
            have it — not that the data does not.

            GEN-117's informational grey: this is a statement of fact about
            what the reader may see, not a warning and not an error.
          */}
          {data.costVisible ? null : (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              {t('reports.costHidden')}
            </p>
          )}
          {/* BR-DSH-07-03: no change pills anywhere on this screen. */}
          <SummaryCards
            cards={[
              ...(data.summary.totalValue === undefined
                ? []
                : [
                    {
                      label: t('reports.cardStockValue'),
                      value: data.summary.totalValue,
                      money: true,
                    },
                  ]),
              { label: t('reports.cardSkus'), value: String(data.summary.skusTracked) },
              ...(data.summary.avgTurnover === undefined
                ? []
                : [
                    {
                      label: t('reports.cardTurnover'),
                      value:
                        data.summary.avgTurnover === null ? '—' : `${data.summary.avgTurnover}×`,
                      valueClassName: 'text-primary',
                    },
                  ]),
              {
                label: t('reports.cardDeadStock'),
                value: String(data.summary.deadCount),
                // BR-DSH-07-06: the one card whose GOOD state is green.
                valueClassName: data.summary.deadCount > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400',
              },
            ]}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('reports.valuation')}</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {data.valuation.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
              ) : (
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-1 text-start">{t('reports.colCode')}</th>
                      <th className="pb-1 text-start">{t('reports.colItem')}</th>
                      <th className="pb-1 text-start">{t('reports.colCategory')}</th>
                      <th className="pb-1 text-end">{t('reports.colOnHand')}</th>
                      {data.costVisible ? (
                        <>
                          <th className="pb-1 text-end">{t('reports.colUnitCost')}</th>
                          <th className="pb-1 text-end">{t('reports.colTotalValue')}</th>
                        </>
                      ) : null}
                      <th className="pb-1 text-start">{t('reports.colMethod')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.valuation.map((row) => (
                      <tr key={row.itemId} className="border-t">
                        <TableCell className="px-0 font-mono text-xs text-muted-foreground"><Ltr value={row.code} /></TableCell>
                        <TableCell className="px-0">{row.name}</TableCell>
                        <TableCell className="px-0 text-muted-foreground">{row.category}</TableCell>
                        {/* `N-4` — §5c's valuation lists every item the company
                            holds, in every unit it holds them in. This is
                            `N-14`'s own column: one heading cannot be true of
                            kilograms and boxes at once, so the unit is on the
                            row. */}
                        <TableCell className="px-0 text-end">
                          <Qty value={row.onHand} precision={row.unitPrecision} unit={row.unitSymbol} />
                        </TableCell>
                        {/* §5c: unit cost keeps its 2 decimals. */}
                        {row.unitCost !== undefined && row.totalValue !== undefined ? (
                          <>
                            <TableCell className="px-0 text-end">
                              <Amount value={row.unitCost} currency={baseCurrency} />
                            </TableCell>
                            <TableCell className="px-0 text-end font-medium">
                              <Amount value={row.totalValue} currency={baseCurrency} />
                            </TableCell>
                          </>
                        ) : null}
                        {/* BR-DSH-07-04: the method belongs to the warehouse, not the item —
                            so it reads from the warehouse master's own label set.
                            BR-DSH-10-18: null is the server saying "no single
                            master record answers" (All warehouses, methods
                            differing between the holding sites) — drawn as the
                            em dash every other not-applicable cell here uses,
                            never substituted with a method no warehouse has. */}
                        <TableCell className="px-0 text-muted-foreground">
                          {row.method === null ? '—' : t(`wh.valuation.${row.method}`)}
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* BR-DSH-07-14: removed, not emptied, without the cost grant. */}
          {data.costVisible ? (
            <div className="grid gap-[18px] lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{t('reports.valueByCategory')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {(data.valueByCategory ?? []).length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
                  ) : (
                    <>
                      <Chart option={donutOption} height={150} />
                      {/* §5d: this donut has NO drill-down, unlike DSH-05's. */}
                      <ul className="mt-2 flex flex-col gap-1 text-sm">
                        {(data.valueByCategory ?? []).map((cat) => (
                          <li key={cat.id ?? cat.name} className="flex justify-between px-1">
                            <span>{cat.name}</span>
                            <span className="flex gap-2 text-muted-foreground">
                              <Amount value={cat.value} currency={baseCurrency} className="text-sm" />
                              <Pct value={cat.share} className="font-mono" />
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{t('reports.valueByWarehouse')}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  {(data.valueByWarehouse ?? []).length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
                  ) : (
                    (data.valueByWarehouse ?? []).map((row) => {
                      const max = Math.max(...(data.valueByWarehouse ?? []).map((w) => Number(w.value)), 1);
                      return (
                        <div key={row.id ?? row.name} className="flex items-center gap-2 text-sm">
                          <span className="w-32 truncate">{row.name}</span>
                          <div className="h-[22px] flex-1 rounded bg-muted">
                            <div className="h-full rounded bg-primary" style={{ width: `${(Number(row.value) / max) * 100}%` }} />
                          </div>
                          <Amount value={row.value} currency={baseCurrency} className="text-sm font-medium" />
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}

          <div className="grid gap-[18px] lg:grid-cols-2">
            {/* BR-DSH-07-14: removed, not emptied, without the cost grant. */}
            {data.costVisible ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{t('reports.turnoverByCategory')}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  {(data.turnoverByCategory ?? []).length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
                  ) : (
                    (data.turnoverByCategory ?? []).map((row) => {
                      // §5e: width is this category against the fastest one.
                      const max = Math.max(...(data.turnoverByCategory ?? []).map((c) => Number(c.turnover)), 0.1);
                      return (
                        <div key={row.name} className="flex items-center gap-2 text-sm">
                          <span className="w-32 truncate">{row.name}</span>
                          <div className="h-[22px] flex-1 rounded bg-muted">
                            <div className="h-full rounded bg-primary" style={{ width: `${Math.min((Number(row.turnover) / max) * 100, 100)}%` }} />
                          </div>
                          <Ltr value={`${row.turnover}×`} className="font-mono text-sm font-medium" />
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            ) : null}

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('reports.aging')}</CardTitle>
                <p className="text-xs text-muted-foreground">{t('reports.agingSub')}</p>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                {data.aging.map((band) => {
                  const max = Math.max(...data.aging.map((b) => Number(b.value ?? 0)), 1);
                  return (
                    <div key={band.bucket} className="flex items-center gap-2 text-sm">
                      <Ltr value={band.bucket} className="w-16 font-mono text-xs" />
                      <div className="h-[22px] flex-1 rounded bg-muted">
                        <div
                          className={cn('h-full rounded', AGING_COLORS[band.bucket])}
                          style={{ width: `${(Number(band.value ?? 0) / max) * 100}%` }}
                        />
                      </div>
                      {band.value === undefined ? null : (
                        <Amount value={band.value} currency={baseCurrency} className="text-sm font-medium" />
                      )}
                      <span className="w-20 text-end text-xs text-muted-foreground">
                        {t('reports.itemCount', { count: band.items })}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">{t('reports.deadStock')}</CardTitle>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-muted-foreground" htmlFor="dead-threshold">
                  {t('reports.noMovementIn')}
                </label>
                <input
                  id="dead-threshold"
                  dir="ltr"
                  className="w-16 rounded-md border bg-background px-2 py-1 text-sm"
                  value={thresholdText}
                  onChange={(e) => setThresholdText(e.target.value)}
                />
                <span className="text-xs text-muted-foreground">{t('reports.days')}</span>
                <select
                  aria-label={t('reports.sortBy')}
                  className="rounded-md border bg-background px-2 py-1 text-xs"
                  value={deadSort}
                  onChange={(e) => setDeadSort(e.target.value as typeof deadSort)}
                >
                  <option value="days">{t('reports.sortDaysIdle')}</option>
                  <option value="value">{t('reports.sortTiedUp')}</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              {/*
                ERR-DSH-07-01: "Set the threshold above every item's idle days
                — `No items exceed the threshold — inventory is moving well.`"
                The sentence is the spec's, verbatim, and its TONE is the rule:
                an empty dead-stock table is GOOD NEWS, so the line says so
                rather than reporting an absence. That is EC-19-01's point
                about wording an empty state "for that particular list" — the
                shared "No rows" would have read as a failure to find
                something, on the one table nobody wants filled.
              */}
              {deadStock.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noDeadStock')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-1 text-start">{t('reports.colCode')}</th>
                      <th className="pb-1 text-start">{t('reports.colItem')}</th>
                      <th className="pb-1 text-end">{t('reports.colOnHand')}</th>
                      <th className="pb-1 text-end">{t('reports.colLastMovement')}</th>
                      <th className="pb-1 text-end">{t('reports.colDaysIdle')}</th>
                      <th className="pb-1 text-end">{t('reports.colTiedUp')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deadStock.map((row) => (
                      // BR-DSH-07-08: past 90 days the WHOLE row tints, whatever
                      // the threshold is set to.
                      <tr key={row.itemId} className={cn('border-t', row.daysIdle > 90 && 'bg-red-50 dark:bg-red-950/30')}>
                        <TableCell className="px-0 font-mono text-xs text-muted-foreground"><Ltr value={row.code} /></TableCell>
                        <TableCell className="px-0">{row.name}</TableCell>
                        {/* §5g's dead stock is still stock: `N-4` says which
                            unit it has been sitting there in. */}
                        <TableCell className="px-0 text-end">
                          <Qty value={row.onHand} precision={row.unitPrecision} unit={row.unitSymbol} />
                        </TableCell>
                        <TableCell className="px-0 text-end">
                          {row.lastMovement ? <Ltr value={row.lastMovement} className="font-mono text-xs" /> : '—'}
                        </TableCell>
                        <TableCell className="px-0 text-end"><Days value={row.daysIdle} className="font-mono" /></TableCell>
                        {row.tiedUpValue === undefined ? null : (
                          <TableCell className="px-0 text-end font-medium">
                            <Amount value={row.tiedUpValue} currency={baseCurrency} />
                          </TableCell>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* BR-DSH-07-14: removed, not emptied, without the cost grant. */}
          {data.costVisible ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('reports.abc')}</CardTitle>
                <p className="text-xs text-muted-foreground">{t('reports.abcHint')}</p>
              </CardHeader>
              <CardContent>
                {/* §5h: the class-count line, each count in its class colour. */}
                <p className="mb-2 flex flex-wrap gap-3 text-xs">
                  {(['A', 'B', 'C'] as const).map((cls) => (
                    <span key={cls} className={cn('rounded-full px-2 py-0.5', CLASS_CHIPS[cls])}>
                      {t('reports.classCount', { class: cls, count: classCounts[cls] })}
                    </span>
                  ))}
                </p>
                {(data.abc ?? []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="pb-1 text-start">{t('reports.colClass')}</th>
                        <th className="pb-1 text-start">{t('reports.colCode')}</th>
                        <th className="pb-1 text-start">{t('reports.colItem')}</th>
                        <th className="pb-1 text-end">{t('reports.colValue')}</th>
                        <th className="pb-1 text-end">{t('reports.colShare')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.abc ?? []).map((row) => (
                        <tr key={row.itemId} className="border-t">
                          <TableCell className="px-0">
                            <span className={cn('rounded-full px-2 py-0.5 text-xs', CLASS_CHIPS[row.class])}>{row.class}</span>
                          </TableCell>
                          <TableCell className="px-0 font-mono text-xs text-muted-foreground"><Ltr value={row.code} /></TableCell>
                          <TableCell className="px-0">{row.name}</TableCell>
                          <TableCell className="px-0 text-end font-medium"><Amount value={row.value} currency={baseCurrency} /></TableCell>
                          <TableCell className="px-0 text-end text-muted-foreground"><Pct value={row.share} className="font-mono" /></TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          ) : null}

          <DetailFold count={detailRows.length}>
            <DetailTable<DetailRow>
              report="inventory"
              columns={detailColumns}
              defaults={DEFAULT_COLUMNS}
              selected={columns}
              onSelect={setColumns}
              rows={detailRows}
              rowKey={(r) => r.itemId}
              currentConfig={() => ({
                columns,
                filters: { threshold: String(threshold) },
                // A14 — this screen has no window to save or restore.
                period: { preset: 'asOfNow' },
              })}
              onApplyConfig={(config, name) => {
                if (config.filters.threshold) setThresholdText(config.filters.threshold);
                // The window is DELIBERATELY not restored. A configuration
                // saved before A14 still carries the dead `from`/`to` it was
                // stored with; applying them would put a window back on a
                // screen that has no boxes to show it and no figure that
                // honours it — the exact half-held state the ruling ended.
                // BR-DSH-10-16: remember WHICH configuration, for the heading.
                setAppliedConfig({ name, body: config });
              }}
            />
          </DetailFold>
        </>
      )}
    </div>
  );
}
